import { motion } from "framer-motion";
import { Clock, TrendUp, Bank, WarningCircle } from "@phosphor-icons/react";

const items = [
  { icon: WarningCircle, tone: "bg-orange-50 text-orange-600", title: "Cash Flow Problems", desc: "Working capital dries up when receivables pile up, choking day-to-day operations." },
  { icon: Clock, tone: "bg-rose-50 text-rose-600", title: "Delayed Payments", desc: "Enterprise buyers stretch payments to 60–90 days, leaving smaller vendors stranded." },
  { icon: TrendUp, tone: "bg-amber-50 text-amber-600", title: "Growth Gets Delayed", desc: "Missed opportunities: bigger orders, new hires, and inventory expansion get put on hold." },
  { icon: Bank, tone: "bg-red-50 text-red-600", title: "Traditional Loans Take Too Long", desc: "Endless paperwork, collateral demands, and multi-week approvals kill momentum." },
];

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald-700 mb-3">The Problem</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Waiting for payments shouldn&apos;t slow your business.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Every unpaid invoice is capital you&apos;ve already earned. When it sits idle, so does your growth.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-5 md:gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`problem-card-${i}`}
              className="group rounded-2xl bg-white border border-slate-100 p-8 md:p-9 shadow-soft hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className={`grid place-items-center w-12 h-12 rounded-xl ${it.tone}`}>
                <it.icon size={24} weight="duotone" />
              </div>
              <h3 className="mt-6 font-display text-xl md:text-2xl font-medium text-slate-900">{it.title}</h3>
              <p className="mt-3 text-slate-500 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
